import React, { useState, useEffect, useRef } from 'react';
import {
  Input,
  InputDiv,
  Label,
  SearchOptions,
  Option
} from '../../../../../components/styles/select-style';

interface OptionType {
  id: number | string;
  name: string;
}

interface Props {
  label: string;
  value: string | number | null;
  onChange: (value: string | number | null) => void;
  options: OptionType[];
  disabled?: boolean;
}

const AutocompleteSelect: React.FC<Props> = ({
  label,
  value,
  onChange,
  options,
  disabled
}) => {
  const [search, setSearch] = useState('');
  const [showOptions, setShowOptions] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);

  const inputRef = useRef<HTMLInputElement>(null);
  const optionsRef = useRef<HTMLUListElement>(null);

  useEffect(() => {
    const selected = options.find(opt => String(opt.id) === String(value));
    setSearch(selected?.name || '');
    setIsTyping(false);
    setShowOptions(false);
  }, [value, options]);

  const filteredOptions = isTyping
    ? options.filter(opt =>
        opt.name.toLowerCase().includes(search.toLowerCase())
      )
    : options;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;

      if (
        optionsRef.current &&
        inputRef.current &&
        !optionsRef.current.contains(target) &&
        !inputRef.current.contains(target)
      ) {
        setShowOptions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleSelect = (opt: OptionType) => {
    setSearch(opt.name);
    setShowOptions(false);
    setIsTyping(false);
    onChange(opt.id);
  };

  return (
    <InputDiv>
      <Label>{label}</Label>

      <Input
        ref={inputRef}
        type="text"
        value={search}
        disabled={disabled}
        placeholder={disabled ? 'Selecione antes...' : 'Todas'}
        onFocus={() => {
          if (disabled) return;
          setShowOptions(true);
          setIsTyping(false);
        }}
        onChange={(e) => {
          setSearch(e.target.value);
          setIsTyping(true);
          setShowOptions(true);
          setSelectedIndex(0);
        }}
      />

      {showOptions && !disabled && filteredOptions.length > 0 && (
        <SearchOptions ref={optionsRef}>
          {filteredOptions.map((opt, index) => (
            <Option
              key={opt.id}
              isSelected={index === selectedIndex}
              onClick={() => handleSelect(opt)}
            >
              {opt.name}
            </Option>
          ))}
        </SearchOptions>
      )}
    </InputDiv>
  );
};

export default AutocompleteSelect;